import { router } from '@inertiajs/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { AMBER, FONT_BODY, FONT_DISPLAY, FONT_MONO, TEAL } from './LandingKit';

const tags = ['SEM RASTREADOR', 'SEM INSTALAÇÃO', 'BI NO WHATSAPP'];

const ROUTE_A = 'M 80 660 C 300 620, 360 400, 600 440 S 940 540, 1180 320';
const ROUTE_B = 'M 40 300 C 240 250, 430 360, 660 300 S 1000 180, 1220 250';
const PINS = [[360, 470], [600, 440], [820, 470], [660, 300], [980, 220]];

export default function HeroExperiment() {
  const goToLeads = () => router.visit('/leads/new?source=landing-hero-experiment');
  const goToHow = () => router.visit('/#how');

  return (
    <Box
      id="hero"
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: '95vh' },
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#eef3f2',
        ...theme.applyStyles('dark', { backgroundColor: '#05080b' }),
      })}
    >
      {/* glow: teal + amber */}
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          inset: 0,
          background:
            `radial-gradient(55% 45% at 82% 12%, ${TEAL}26, transparent 70%),` +
            `radial-gradient(40% 38% at 60% 95%, ${AMBER}1c, transparent 70%)`,
          ...theme.applyStyles('dark', {
            background:
              `radial-gradient(55% 45% at 82% 12%, ${TEAL}33, transparent 70%),` +
              `radial-gradient(42% 40% at 58% 100%, ${AMBER}26, transparent 72%)`,
          }),
        })}
      />

      {/* the map: roads, live routes, vehicles, pins */}
      <Box
        aria-hidden
        component="svg"
        viewBox="0 0 1280 760"
        preserveAspectRatio="xMidYMid slice"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: { xs: 0.55, md: 1 } }}
      >
        <defs>
          <filter id="ds-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="5" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <path id="rA" d={ROUTE_A} />
          <path id="rB" d={ROUTE_B} />
        </defs>

        <g fill="none" stroke={TEAL} strokeOpacity="0.10" strokeWidth="1.2">
          <path d="M -50 200 H 1330" />
          <path d="M -50 380 H 1330" />
          <path d="M -50 560 H 1330" />
          <path d="M 260 -40 V 800" />
          <path d="M 560 -40 V 800" />
          <path d="M 880 -40 V 800" />
          <path d="M -40 720 L 600 120 L 1320 520" strokeOpacity="0.07" />
        </g>

        <use href="#rA" fill="none" stroke={TEAL} strokeOpacity="0.22" strokeWidth="2" />
        <use href="#rB" fill="none" stroke={AMBER} strokeOpacity="0.18" strokeWidth="2" />

        <use href="#rA" className="ds-flow" fill="none" stroke={TEAL} strokeWidth="3" strokeLinecap="round" strokeDasharray="2 14" style={{ animationDuration: '1.1s' }} />
        <use href="#rB" className="ds-flow" fill="none" stroke={AMBER} strokeWidth="3" strokeLinecap="round" strokeDasharray="2 16" style={{ animationDuration: '1.5s' }} />

        {PINS.map(([cx, cy], i) => (
          <g key={`${cx}-${cy}`}>
            <circle cx={cx} cy={cy} r="6" fill="none" stroke={i % 2 ? AMBER : TEAL} strokeOpacity="0.6" strokeWidth="1.5">
              <animate attributeName="r" values="6;16;6" dur="2.4s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" values="0.6;0;0.6" dur="2.4s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
            </circle>
            <circle cx={cx} cy={cy} r="3.5" fill={i % 2 ? AMBER : TEAL} />
          </g>
        ))}

        <g filter="url(#ds-glow)">
          <circle r="6" fill={TEAL}>
            <animateMotion dur="7s" repeatCount="indefinite" rotate="auto"><mpath href="#rA" /></animateMotion>
          </circle>
        </g>
        <g filter="url(#ds-glow)">
          <circle r="6" fill={AMBER}>
            <animateMotion dur="9s" repeatCount="indefinite" rotate="auto"><mpath href="#rB" /></animateMotion>
          </circle>
        </g>
      </Box>

      {/* floating live-tracking card */}
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          right: '6%',
          top: '50%',
          transform: 'translateY(-50%)',
          display: { xs: 'none', lg: 'block' },
          width: 268,
          p: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(2,20,18,0.10)',
          backgroundColor: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(14px)',
          boxShadow: '0 24px 60px rgba(2,20,18,0.18)',
          animation: 'ds-rise 0.8s cubic-bezier(0.22,1,0.36,1) 0.5s forwards',
          opacity: 0,
          ...theme.applyStyles('dark', {
            borderColor: 'rgba(255,255,255,0.10)',
            backgroundColor: 'rgba(12,18,20,0.55)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          }),
        })}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: TEAL, animation: 'ds-blink 1.4s ease-in-out infinite' }} />
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.12em', color: 'text.secondary' }}>
            RASTREAMENTO AO VIVO
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${TEAL}, #008f80)`, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, lineHeight: 1.2, color: 'text.primary' }}>
              João • Rota Centro
            </Typography>
            <Typography sx={{ fontSize: 12, color: AMBER, fontWeight: 600 }}>● A caminho</Typography>
          </Box>
        </Stack>
        <Box component="svg" viewBox="0 0 240 44" sx={{ width: '100%', height: 40, display: 'block', mb: 1 }}>
          <path d="M4 34 C 50 34, 70 10, 120 18 S 200 30, 236 8" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="2 7" />
          <circle cx="120" cy="18" r="3.5" fill={AMBER} />
        </Box>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          {['Coleta confirmada', 'Próxima parada'].map((c, i) => (
            <Box key={c} sx={{ fontFamily: FONT_MONO, fontSize: 10, px: 1, py: 0.4, borderRadius: 1, border: '1px solid', borderColor: i ? `${AMBER}55` : `${TEAL}55`, color: i ? AMBER : TEAL }}>
              {c}
            </Box>
          ))}
        </Stack>
      </Box>

      {/* content */}
      <Container sx={{ position: 'relative', py: { xs: 16, md: 0 } }}>
        <Stack spacing={3.5} sx={{ maxWidth: 900 }}>
          <Box
            className="ds-rise"
            sx={(theme) => ({
              animationDelay: '0.05s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              alignSelf: 'flex-start',
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              border: '1px solid',
              borderColor: `${TEAL}55`,
              backgroundColor: `${TEAL}14`,
              fontFamily: FONT_MONO,
              fontSize: 12,
              letterSpacing: '0.12em',
              color: '#0a3f39',
              ...theme.applyStyles('dark', { color: '#7df0e2' }),
            })}
          >
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TEAL, animation: 'ds-blink 1.4s ease-in-out infinite' }} />
            AO VIVO · CONTROLE DE COLETAS
          </Box>

          <Typography
            component="h1"
            className="ds-rise"
            sx={(theme) => ({
              animationDelay: '0.15s',
              fontFamily: FONT_DISPLAY,
              fontWeight: 800,
              lineHeight: 0.98,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              fontSize: 'clamp(2.1rem, 5vw, 4rem)',
            })}
          >
            <Box component="span" sx={(theme) => ({ display: 'block', whiteSpace: { md: 'nowrap' }, color: '#071513', ...theme.applyStyles('dark', { color: '#f2fbf9' }) })}>
              Acompanhe cada coleta
            </Box>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                color: TEAL,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: '0.04em',
                  height: '0.08em',
                  background: TEAL,
                  borderRadius: 2,
                  boxShadow: `0 0 18px ${TEAL}`,
                },
              }}
            >
              em tempo real
            </Box>
          </Typography>

          <Typography
            className="ds-rise"
            sx={(theme) => ({
              animationDelay: '0.25s',
              maxWidth: 540,
              fontFamily: FONT_BODY,
              fontSize: { xs: '1.05rem', md: '1.2rem' },
              lineHeight: 1.5,
              color: '#33514c',
              ...theme.applyStyles('dark', { color: '#9fc4bd' }),
            })}
          >
            Toda a sua operação de coletas numa torre de controle que se atualiza
            sozinha, em tempo real. E a IA no WhatsApp responde qualquer pergunta sobre
            ela a qualquer hora, sem esperar por ninguém.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} className="ds-rise" sx={{ animationDelay: '0.35s', pt: 1 }}>
            <Button
              onClick={goToLeads}
              disableElevation
              sx={{
                px: 3.5,
                py: 1.4,
                borderRadius: 999,
                fontFamily: FONT_BODY,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '1rem',
                color: '#04110f',
                background: `linear-gradient(180deg, ${TEAL}, #00a594)`,
                boxShadow: `0 10px 30px ${TEAL}40`,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 40px ${TEAL}66`, background: `linear-gradient(180deg, ${TEAL}, #00a594)` },
              }}
            >
              Agendar demonstração
            </Button>
            <Button
              onClick={goToHow}
              sx={(theme) => ({
                px: 3,
                py: 1.4,
                borderRadius: 999,
                fontFamily: FONT_BODY,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                color: '#0a3f39',
                border: '1px solid',
                borderColor: 'rgba(2,20,18,0.18)',
                '&:hover': { borderColor: TEAL, backgroundColor: `${TEAL}10` },
                ...theme.applyStyles('dark', { color: '#cfeae5', borderColor: 'rgba(255,255,255,0.18)' }),
              })}
            >
              Ver como funciona
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" className="ds-rise" sx={{ animationDelay: '0.45s', pt: 2 }}>
            {tags.map((t) => (
              <Box
                key={t}
                sx={(theme) => ({
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  letterSpacing: '0.1em',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'rgba(2,20,18,0.12)',
                  color: '#3a5651aa',
                  ...theme.applyStyles('dark', { borderColor: 'rgba(255,255,255,0.12)', color: '#8fb3ac' }),
                })}
              >
                {t}
              </Box>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
